import type { FieldErrors } from 'react-hook-form';
import get from 'lodash/get';
import { ConfigRegistryConfig, ConfigRegistryType } from 'types/apis/pilotInterfaces';
import { filterConfigsByGroupSelection, isMultiSelectRenderType, isBooleanRenderType, isFuelKeyedMap, FUEL_TYPE_DEP_REF } from './registryUtils';
import { parsePossibleValues } from './dynamicConfigRegistryParsing';

const MULTISELECT_DROPDOWN_EXCEPTION = {
  configType: 'frontend_configs',
  configKey: 'supported_locale',
} as const;

function isFrontendSupportedLocaleField(
  cfg: ConfigRegistryConfig | undefined,
  configType?: string
): boolean {
  if (!cfg) return false;
  return (
    String(configType || '').toLowerCase() === MULTISELECT_DROPDOWN_EXCEPTION.configType &&
    String(cfg.configKey || '').toLowerCase() === MULTISELECT_DROPDOWN_EXCEPTION.configKey
  );
}

/** Same semantics as DynamicConfigForm: registry-driven "required" for validation */
export function isDynamicRegistryFieldMandatory(cfg: ConfigRegistryConfig): boolean {
  return cfg?.isMandatoryField === true;
}

function isRegistryRef(x: any): x is { configType?: string; configKey?: string } {
  return x && typeof x === 'object' && 'configKey' in x;
}

function buildConfigRefMap(configRegistryData: ConfigRegistryType[]): Map<string, { type: string; key: string }> {
  const map = new Map<string, { type: string; key: string }>();
  configRegistryData.forEach((ct) => {
    ct.configs.forEach((cfg) => {
      map.set(`${ct.configType}.${cfg.configKey}`, { type: ct.configType, key: cfg.configKey });
      map.set(cfg.configKey, { type: ct.configType, key: cfg.configKey });
    });
  });
  return map;
}

function resolveFieldRef(
  ref: { configType?: string; configKey?: string },
  currentType: string,
  configRefMap: Map<string, { type: string; key: string }>
): string | null {
  if (!ref) return null;
  const rt = ref.configType || currentType;
  const rk = ref.configKey;
  if (!rk) return null;
  const exact = configRefMap.get(`${rt}.${rk}`);
  if (exact) return `${exact.type}.${exact.key}`;
  const rtLc = String(rt).toLowerCase();
  const rkLc = String(rk).toLowerCase();
  let found: string | null = null;
  configRefMap.forEach((v) => {
    if (!found && String(v.type).toLowerCase() === rtLc && String(v.key).toLowerCase() === rkLc) {
      found = `${v.type}.${v.key}`;
    }
  });
  if (found) return found;
  // Ref has no explicit configType (e.g. FUEL_TYPE_DEP_REF) — match by configKey alone rather
  // than requiring it live under the same type bucket as the dependent config.
  if (!ref.configType) {
    const anyType = configRefMap.get(rk);
    if (anyType) return `${anyType.type}.${anyType.key}`;
  }
  return null;
}

const equalsIgnoreCase = (a: string, b: string) =>
  String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

function matchesFuelKey(selected: string, key: string): boolean {
  if (equalsIgnoreCase(selected, key)) return true;
  if (key === 'ELECTRIC' && (equalsIgnoreCase(selected, 'Electricity') || equalsIgnoreCase(selected, 'Electric')))
    return true;
  if (key === 'GAS' && equalsIgnoreCase(selected, 'Gas')) return true;
  if (key === 'WATER' && equalsIgnoreCase(selected, 'Water')) return true;
  return false;
}

function getSelectedDependencyKeys(
  depRef: any,
  currentType: string,
  values: Record<string, any>,
  additionalConfigData: Record<string, any> | undefined,
  configRefMap: Map<string, { type: string; key: string }>
): Set<string> {
  const set = new Set<string>();
  const parentRef = resolveFieldRef(depRef as any, currentType, configRefMap);
  if (!parentRef) return set;
  let parentVal: any = get(values, parentRef);
  if ((Array.isArray(parentVal) && parentVal.length === 0) || parentVal === '' || parentVal == null) {
    parentVal = additionalConfigData?.[parentRef];
  }
  if (Array.isArray(parentVal)) {
    parentVal.forEach((v) => {
      const s = String(v);
      if (matchesFuelKey(s, 'ELECTRIC')) set.add('ELECTRIC');
      if (matchesFuelKey(s, 'GAS')) set.add('GAS');
      if (matchesFuelKey(s, 'WATER')) set.add('WATER');
    });
  } else if (typeof parentVal === 'string' && parentVal) {
    const s = parentVal;
    if (matchesFuelKey(s, 'ELECTRIC')) set.add('ELECTRIC');
    if (matchesFuelKey(s, 'GAS')) set.add('GAS');
    if (matchesFuelKey(s, 'WATER')) set.add('WATER');
  }
  return set;
}

function shouldShowConfig(
  config: ConfigRegistryConfig,
  currentType: string,
  values: Record<string, any>,
  additionalConfigData: Record<string, any> | undefined,
  configRefMap: Map<string, { type: string; key: string }>
): boolean {
  const depends = (config as any).configDependsUpon as any;
  if (!depends) return true;

  let refs: any[] = [];
  if (Array.isArray(depends)) refs = depends;
  else if (isRegistryRef(depends)) refs = [depends];
  else if (typeof depends === 'object') refs = Object.values(depends);

  for (const r of refs) {
    if (isRegistryRef(r)) {
      const selected = getSelectedDependencyKeys(r, currentType, values, additionalConfigData, configRefMap);
      if (selected.size > 0) return true;
    }
    const parentField = resolveFieldRef(r as any, currentType, configRefMap);
    if (!parentField) continue;
    const val = get(values, parentField);
    if (Array.isArray(val)) {
      if (val.length > 0) return true;
    } else if (typeof val === 'boolean') {
      if (val) return true;
    } else if (typeof val === 'string') {
      if (val.trim() !== '') return true;
    } else if (val && typeof val === 'object') {
      if (Object.keys(val).length > 0) return true;
    }
  }
  return false;
}

function setNestedFieldError(errors: FieldErrors, fieldPath: string, message: string): void {
  const parts = fieldPath.split('.');
  let cur: any = errors;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    cur[p] = cur[p] || {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = { type: 'validate', message };
}

function getRegistryConfigsForValidation(
  configRegistryData: ConfigRegistryType[],
  values: Record<string, any>
): ConfigRegistryType[] {
  const groupSelection = (values.configGroupSelection as { [key: string]: string }) || {};

  // Only validate what is actually rendered: configs for the selected data source.
  // Order is irrelevant here — errors are keyed by field path.
  return filterConfigsByGroupSelection(configRegistryData, groupSelection).filter(
    (configType) => configType.configs.length > 0
  );
}

function getMandatoryFieldMessage(
  config: ConfigRegistryConfig,
  configType: string,
  value: any,
  values: Record<string, any>,
  additionalConfigData: Record<string, any> | undefined,
  configRefMap: Map<string, { type: string; key: string }>
): string | null {
  const parsedValues = parsePossibleValues(config.possibleValues);
  const isMapStructure = parsedValues.type === 'map';
  const possibleValuesArray = parsedValues.type === 'simple' ? parsedValues.data : [];
  const mapData = parsedValues.type === 'map' ? parsedValues.data : {};

  const hasPossibleValues = possibleValuesArray.length > 0 || isMapStructure;
  const isMapKeysMultiSelect = isMapStructure && isMultiSelectRenderType(config?.renderType);
  const isMapMultiselect = isMapStructure && !isMapKeysMultiSelect;
  const isSelect = hasPossibleValues && !isMapStructure;
  const isBoolean = !hasPossibleValues && isBooleanRenderType(config?.renderType);

  if (!shouldShowConfig(config, configType, values, additionalConfigData, configRefMap)) {
    return null;
  }

  if (isMapKeysMultiSelect) {
    if (!isDynamicRegistryFieldMandatory(config)) return null;
    const ok = Array.isArray(value) && value.length > 0;
    return ok ? null : 'This field is required.';
  }

  if (isMapMultiselect) {
    if (!isDynamicRegistryFieldMandatory(config)) return null;
    const depends = config.configDependsUpon;
    const mapKeys = Object.keys(mapData || {});
    let keysToRender: string[] = [];
    // Must mirror DynamicConfigForm exactly, or submit demands fuels the form never rendered
    if (depends || isFuelKeyedMap(mapKeys)) {
      const depRef = !depends
        ? FUEL_TYPE_DEP_REF
        : Array.isArray(depends)
          ? depends[0]
          : isRegistryRef(depends)
            ? depends
            : Object.values(depends as any)[0];
      const selectedKeys = getSelectedDependencyKeys(depRef, configType, values, additionalConfigData, configRefMap);
      // Mirror DynamicConfigForm's case-insensitive match, or validation demands a fuel section the form never rendered
      keysToRender = mapKeys.filter((k) => selectedKeys.has(String(k).toUpperCase()));
      if (keysToRender.length === 0) return null;
    } else {
      keysToRender = mapKeys;
    }
    if (!keysToRender.length) return null;
    const allSelected = keysToRender.every(
      (k) => value && String((value as any)[k] || '').trim() !== ''
    );
    return allSelected ? null : 'Please select an option for all visible sections.';
  }

  if (isBoolean) {
    return null;
  }

  if (isSelect) {
    if (!isDynamicRegistryFieldMandatory(config)) return null;
    const isMulti = isMultiSelectRenderType(config?.renderType) || isFrontendSupportedLocaleField(config, configType);
    if (isMulti) {
      const ok = Array.isArray(value) && value.length > 0;
      return ok ? null : 'This field is required.';
    }
    const ok = typeof value === 'string' && value.trim() !== '';
    return ok ? null : 'This field is required.';
  }

  if (!isDynamicRegistryFieldMandatory(config)) return null;
  if (value === undefined || value === null) return 'This field is required.';
  if (typeof value === 'string' && value.trim() === '') return 'This field is required.';
  return null;
}

/**
 * Validates mandatory registry fields that use Controller `rules` in DynamicConfigForm.
 * Must be merged into the zod resolver: when a resolver is set, react-hook-form does not run
 * field-level `rules` on submit.
 */
export function validateDynamicRegistryMandatoryFields(
  values: Record<string, any>,
  configRegistryData: ConfigRegistryType[],
  additionalConfigData?: Record<string, any>,
  readOnlyConfigKeys?: string[]
): FieldErrors {
  const errors: FieldErrors = {};
  if (!configRegistryData?.length) return errors;

  const configRefMap = buildConfigRefMap(configRegistryData);
  const filteredRegistry = getRegistryConfigsForValidation(configRegistryData, values);

  for (const ct of filteredRegistry) {
    for (const cfg of ct.configs) {
      // Already answered (and locked) on an earlier step — DynamicConfigForm renders it read-only
      // with no way to surface a field error, so demanding it here would block submit silently.
      if (readOnlyConfigKeys?.includes(cfg.configKey)) continue;
      const fieldName = `${ct.configType}.${cfg.configKey}`;
      const raw = get(values, fieldName);
      const msg = getMandatoryFieldMessage(cfg, ct.configType, raw, values, additionalConfigData, configRefMap);
      if (msg) {
        setNestedFieldError(errors, fieldName, msg);
      }
    }
  }

  return errors;
}
